<template>
  <div class="customer-info-section">
    <hr
      class="hr-text"
      data-content="Thông tin khách hàng"
      style="font-weight: 600"
    />

    <!-- Loại KH: radio switcher (editableType) hoặc label read-only -->
    <b-row class="mb-2">
      <b-col cols="12" class="d-flex align-items-center">
        <template v-if="editableType">
          <label class="mb-0 mr-3 font-weight-bold">Loại KH:</label>
          <b-form-radio-group
            :checked="customer.customerType"
            :options="customerTypeOptions"
            :disabled="disabled || disabledType"
            size="sm"
            @change="customer.customerType = $event"
          />
        </template>
        <small v-else class="text-danger font-italic">
          ({{ isBusiness ? 'Doanh nghiệp' : 'Khách lẻ' }})
        </small>
      </b-col>
    </b-row>

    <!-- ===== Cá nhân (CUSTOMER) ===== -->
    <template v-if="!isBusiness">
      <!-- Row 1: 4-col grid cân bằng (3/3/3/3) -->
      <b-row>
        <b-col cols="3">
          <b-form-group>
            <label>
              Điện thoại:
              <b v-if="showRequiredMark" class="text-danger">*</b>
            </label>
            <slot name="phone-customer">
              <b-form-input
                :value="customer.customerPhone"
                size="sm"
                :disabled="disabled"
                :placeholder="ph('Số điện thoại')"
                class="input-style"
                @input="customer.customerPhone = $event"
              />
            </slot>
          </b-form-group>
        </b-col>
        <b-col cols="3">
          <b-form-group>
            <label>
              Họ và tên:
              <b v-if="showRequiredMark" class="text-danger">*</b>
            </label>
            <b-form-input
              :value="customer.customerName"
              size="sm"
              :disabled="disabled"
              :placeholder="ph('Tên khách hàng')"
              class="input-style"
              @input="customer.customerName = $event"
            />
          </b-form-group>
        </b-col>
        <b-col cols="3">
          <b-form-group>
            <label>
              Danh xưng:
              <b v-if="showRequiredMark" class="text-danger">*</b>
            </label>
            <b-form-select
              v-if="!disabled"
              :value="customer.customerGender"
              :options="appellationOptions"
              size="sm"
              @change="customer.customerGender = $event"
            />
            <b-form-input
              v-else
              :value="appellationLabel(customer.customerGender)"
              size="sm"
              disabled
            />
          </b-form-group>
        </b-col>
        <b-col cols="3">
          <b-form-group>
            <label>Ngày sinh:</label>
            <date-picker
              v-if="!disabled"
              class="form-control form-control-sm"
              :config="dpConfig"
              :value="customer.customerDob"
              :placeholder="ph('Ngày sinh')"
              @input="customer.customerDob = $event"
            ></date-picker>
            <b-form-input
              v-else
              :value="formatDate(customer.customerDob)"
              size="sm"
              disabled
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Row 2: 4-col grid cân bằng (3/3/3/3) — cột thẳng hàng với Row 1 -->
      <b-row>
        <b-col cols="3">
          <b-form-group>
            <label>Email:</label>
            <b-form-input
              :value="customer.customerEmail"
              size="sm"
              :disabled="disabled"
              :placeholder="ph('email@example.com')"
              class="input-style"
              @input="customer.customerEmail = $event"
            />
          </b-form-group>
        </b-col>
        <b-col cols="3">
          <b-form-group>
            <label>
              Số CCCD/CMND:
              <b v-if="showRequiredMark" class="text-danger">*</b>
            </label>
            <b-form-input
              :value="customer.customerCccdNo"
              size="sm"
              :disabled="disabled"
              :placeholder="ph('VD: 030123456789')"
              class="input-style"
              :state="errors.cccdNo ? false : null"
              @input="customer.customerCccdNo = $event"
            />
            <b-form-invalid-feedback :state="!errors.cccdNo">
              {{ errors.cccdNo }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col cols="3">
          <b-form-group>
            <label>
              Ngày cấp CCCD:
              <b v-if="showRequiredMark" class="text-danger">*</b>
            </label>
            <date-picker
              v-if="!disabled"
              class="form-control form-control-sm"
              :config="dpConfig"
              :value="customer.customerCccdDate"
              :placeholder="ph('Chọn ngày cấp')"
              @input="customer.customerCccdDate = $event"
            ></date-picker>
            <b-form-input
              v-else
              :value="formatDate(customer.customerCccdDate)"
              size="sm"
              disabled
            />
            <small v-if="errors.cccdDate" class="text-danger">
              {{ errors.cccdDate }}
            </small>
          </b-form-group>
        </b-col>
        <b-col cols="3">
          <b-form-group>
            <label>
              Nơi cấp CCCD:
              <b v-if="showRequiredMark" class="text-danger">*</b>
            </label>
            <b-form-input
              :value="customer.customerCccdPlace"
              size="sm"
              :disabled="disabled"
              :placeholder="ph('VD: Cục CSQLHC về TTXH')"
              class="input-style"
              :state="errors.cccdPlace ? false : null"
              @input="customer.customerCccdPlace = $event"
            />
            <b-form-invalid-feedback :state="!errors.cccdPlace">
              {{ errors.cccdPlace }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Row 3: Địa chỉ -->
      <b-row>
        <b-col cols="12">
          <b-form-group>
            <label>Địa chỉ:</label>
            <b-form-textarea
              :value="customer.customerAddress"
              size="sm"
              :rows="2"
              :max-rows="4"
              :disabled="disabled"
              :placeholder="ph('Địa chỉ khách hàng')"
              class="input-style"
              @input="customer.customerAddress = $event"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </template>

    <!-- ===== Doanh nghiệp (BUSSINESS) ===== -->
    <template v-else>
      <!-- Row 1: MST | Tên DN | Ngày thành lập -->
      <b-row>
        <b-col cols="3">
          <b-form-group>
            <label>
              <!-- Input MST: bind thẳng customer.customerMst. Search DN
                   (`GET /businesses?search=`) đã match cả phoneNo + taxNo. -->
              Mã số thuế:
              <b v-if="showRequiredMark" class="text-danger">*</b>
            </label>
            <slot name="mst-business">
              <b-form-input
                :value="customer.customerMst"
                size="sm"
                :disabled="disabled"
                :placeholder="ph('Mã số thuế')"
                class="input-style"
                @input="customer.customerMst = $event"
              />
            </slot>
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group>
            <label>
              Tên doanh nghiệp:
              <b v-if="showRequiredMark" class="text-danger">*</b>
            </label>
            <b-form-input
              :value="customer.customerName"
              size="sm"
              :disabled="disabled"
              :placeholder="ph('Tên doanh nghiệp')"
              class="input-style"
              @input="customer.customerName = $event"
            />
          </b-form-group>
        </b-col>
        <b-col cols="3">
          <b-form-group>
            <label>Ngày thành lập:</label>
            <date-picker
              v-if="!disabled"
              class="form-control form-control-sm"
              :config="dpConfig"
              :value="customer.customerFoundDate"
              :placeholder="ph('Ngày thành lập')"
              @input="customer.customerFoundDate = $event"
            ></date-picker>
            <b-form-input
              v-else
              :value="formatDate(customer.customerFoundDate)"
              size="sm"
              disabled
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Row 2: 4-col grid cân bằng (3/3/3/3) -->
      <b-row>
        <b-col cols="3">
          <b-form-group>
            <label>Email:</label>
            <b-form-input
              :value="customer.customerEmail"
              size="sm"
              :disabled="disabled"
              :placeholder="ph('email@company.com')"
              class="input-style"
              @input="customer.customerEmail = $event"
            />
          </b-form-group>
        </b-col>
        <b-col cols="3">
          <b-form-group>
            <label>
              Người đại diện:
              <b v-if="showRequiredMark" class="text-danger">*</b>
            </label>
            <b-form-input
              :value="customer.customerRepresentative"
              size="sm"
              :disabled="disabled"
              :placeholder="ph('Họ tên người đại diện')"
              class="input-style"
              :state="errors.representative ? false : null"
              @input="customer.customerRepresentative = $event"
            />
            <b-form-invalid-feedback :state="!errors.representative">
              {{ errors.representative }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col cols="3">
          <b-form-group>
            <label>
              Chức vụ:
              <b v-if="showRequiredMark" class="text-danger">*</b>
            </label>
            <b-form-input
              :value="customer.customerRepPosition"
              size="sm"
              :disabled="disabled"
              :placeholder="ph('Giám đốc / Tổng GĐ')"
              class="input-style"
              :state="errors.repPosition ? false : null"
              @input="customer.customerRepPosition = $event"
            />
            <b-form-invalid-feedback :state="!errors.repPosition">
              {{ errors.repPosition }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col cols="3">
          <b-form-group>
            <label>Điện thoại:</label>
            <b-form-input
              :value="customer.customerPhone"
              size="sm"
              :disabled="disabled"
              :placeholder="ph('SĐT')"
              class="input-style"
              @input="customer.customerPhone = $event"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Row 3: Giấy ủy quyền số | Ngày ủy quyền (6/6 cân bằng) -->
      <b-row>
        <b-col cols="6">
          <b-form-group>
            <label>Giấy ủy quyền số:</label>
            <b-form-input
              :value="customer.authorizationNo"
              size="sm"
              :disabled="disabled"
              :placeholder="ph('Số giấy ủy quyền (nếu có)')"
              class="input-style"
              @input="customer.authorizationNo = $event"
            />
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group>
            <label>Ngày ủy quyền:</label>
            <date-picker
              v-if="!disabled"
              class="form-control form-control-sm"
              :config="dpConfig"
              :value="customer.authorizationDate"
              :placeholder="ph('Ngày ủy quyền')"
              @input="customer.authorizationDate = $event"
            ></date-picker>
            <b-form-input
              v-else
              :value="formatDate(customer.authorizationDate)"
              size="sm"
              disabled
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Row 4: Địa chỉ -->
      <b-row>
        <b-col cols="12">
          <b-form-group>
            <label>Địa chỉ trụ sở:</label>
            <b-form-textarea
              :value="customer.customerAddress"
              size="sm"
              :rows="2"
              :max-rows="4"
              :disabled="disabled"
              :placeholder="ph('Địa chỉ đăng ký kinh doanh')"
              class="input-style"
              @input="customer.customerAddress = $event"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import datePicker from 'vue-bootstrap-datetimepicker';
import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css';
import jQuery from 'jquery';
import '@fortawesome/fontawesome-free/css/fontawesome.css';
import '@fortawesome/fontawesome-free/css/regular.css';
import '@fortawesome/fontawesome-free/css/solid.css';
import moment from 'moment';

// Setup datepicker icons (pattern AddCustomer.vue) — global defaults, chạy 1 lần.
jQuery.extend(true, jQuery.fn.datetimepicker.defaults, {
  icons: {
    time: 'far fa-clock',
    date: 'far fa-calendar',
    up: 'fas fa-arrow-up',
    down: 'fas fa-arrow-down',
    previous: 'fas fa-chevron-left',
    next: 'fas fa-chevron-right',
    today: 'fas fa-calendar-check',
    clear: 'far fa-trash-alt',
    close: 'far fa-times-circle',
  },
});

/**
 * CustomerInfoSection — section "Thông tin khách hàng" dùng chung cho:
 *  - `pages/contracts/Upsert.vue` (HĐMB) — mode read-only (disabled=true)
 *  - `pages/contract-request/Upsert.vue` (Đề Nghị tạo HĐ) — mode editable
 *
 * Component MUTATE prop `customer` trực tiếp (pattern codebase — giống ContractInfo,
 * Discount...). Parent nào có state nằm rải rác (vd Đề Nghị: customerLegal/customerEmail
 * tách riêng) thì dùng 1 computed-proxy object với getter/setter để forward.
 *
 * Unified shape của `customer` (prefix `customer*`):
 *   customerType, customerPhone, customerName, customerGender, customerEmail,
 *   customerAddress, customerDob, customerCccdNo, customerCccdDate, customerCccdPlace,
 *   customerMst, customerFoundDate, customerRepresentative, customerRepPosition,
 *   authorizationNo, authorizationDate
 *
 * Slots:
 *   - #phone-customer: thay input SĐT (cá nhân) — vd Autosuggest trong Đề Nghị
 *   - #mst-business: thay input MST (DN) — vd Autosuggest trong Đề Nghị
 */
export default {
  name: 'CustomerInfoSection',
  components: { datePicker },
  props: {
    customer: { type: Object, required: true },
    // true → toàn bộ field disabled (read-only). HĐMB dùng true.
    disabled: { type: Boolean, default: false },
    // true → hiện radio chọn Loại KH (CN/DN). false → chỉ label.
    editableType: { type: Boolean, default: false },
    // true → disable riêng radio Loại KH (vd: edit Đề Nghị không cho đổi loại).
    disabledType: { type: Boolean, default: false },
    // Validation errors { cccdNo, cccdDate, cccdPlace, representative, repPosition }
    errors: { type: Object, default: () => ({}) },
    // true → hiện dấu * cho field bắt buộc (chỉ ý nghĩa khi không disabled).
    showRequired: { type: Boolean, default: false },
    appellationOptions: {
      type: Array,
      default: () => [
        { value: null, text: 'Chọn danh xưng' },
        { value: 1, text: 'Anh' },
        { value: 2, text: 'Chị' },
        { value: 3, text: 'Khác' },
      ],
    },
    customerTypeOptions: {
      type: Array,
      default: () => [
        { value: 'CUSTOMER', text: 'Cá nhân' },
        { value: 'BUSSINESS', text: 'Doanh nghiệp' },
      ],
    },
    dpConfig: {
      type: Object,
      default: () => ({
        format: 'DD/MM/YYYY',
        useCurrent: false,
        showClear: true,
        showClose: true,
        minDate: false,
        maxDate: false,
        locale: 'vi',
        sideBySide: true,
      }),
    },
  },
  computed: {
    isBusiness() {
      return this.customer && this.customer.customerType === 'BUSSINESS';
    },
    showRequiredMark() {
      return this.showRequired && !this.disabled;
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return '—';
      // Hỗ trợ cả ISO string từ BE lẫn 'DD/MM/YYYY' từ datepicker.
      const m1 = moment(value, ['DD/MM/YYYY', moment.ISO_8601], true);
      const m = m1.isValid() ? m1 : moment(value);
      return m.isValid() ? m.format('DD/MM/YYYY') : '—';
    },
    appellationLabel(value) {
      const map = { 1: 'Anh', 2: 'Chị', 3: 'Khác' };
      return map[Number(value)] || '—';
    },
    /**
     * Placeholder: mode editable → text gợi ý; mode disabled → '—' (read-only,
     * tránh hiểu nhầm field đang chờ nhập).
     */
    ph(text) {
      return this.disabled ? '—' : text;
    },
  },
};
</script>

<style lang="scss" scoped>
.customer-info-section {
  .input-style {
    border-radius: 0.28rem;
  }
}
</style>
